<template lang="pug">
.start-claim.overlay
  .container.mt-5
    .row.mt-4(v-if="formSubmitted")
      .col-sm-12.offset-md-3.col-md-6(v-if="formType === 'reimbursement-claim-form'")
        div.mt-5
          .d-flex.flex-column.justify-conent-center.align-items-center.p-2
            img(:src="require(`@/assets/emojis/high-five.svg`)")
            h3.h3.font-hergon.mt-3.text-center
              | Your request for a reimbursement claim was received
            p.text-gray-900.mt-3.text-center.font-lg.font-weight-medium
              | To continue your reimbursement process, start
              br
              | uploading the required documents.
            n-button.w-100.mb-3(
              buttonText="View documents list ->",
              variant="outline-primary",
              @click="$bvModal.show('document-check-list')")
            b-modal(id="document-check-list", no-footer, title="Documents List", hide-footer, centered)
              template(slot='modal-header')
                .d-flex.justify-content-between.align-items-center.w-100
                  .py-1.d-flex.align-items-center
                    span.font-lg.font-weight-semibold.mr-2 Documents List
                  .py-1
                    b-avatar.shadow-none(variant="none", button, @click="$bvModal.hide('document-check-list')")
                      i.icon-cross
              .p-2
                .d-flex.justify-content-between.align-items-center.mb-4(v-for="document in claimDocumentsList")
                  span.font-weight-medium.text-gray-900.font-md(
                    v-if="shouldShowLink(document)") {{ document }} ->
                    b-link(:href="claimFormsLink", target="_blank") &nbsp;Download
                  span.font-weight-medium.text-gray-900.font-md(v-else) {{ document }}
            n-button.w-100.mb-2(
              buttonText="Continue to upload documents ->",
              variant="secondary",
              :to="{name: 'upload-documents', params: {id: claimAssist.id }}")
            p.text-gray-800.mt-4.mb-5.text-center
              | You can track your claim status from the
              br
              | claims page on the Nova Portal
      .col-md-12(v-else)
        .offset-md-2.col-md-8.d-flex.flex-column.justify-conent-center.align-items-center.p-2
          img(:src="require(`@/assets/emojis/high-five.svg`)")
          h3.h3.font-hergon.mt-3
            | You’re all set ✨
          p.text-gray-900.mt-1.text-center.font-lg.font-weight-medium
            | Your claim request has been created.
          p.text-gray-900.mt-1.text-center.font-lg.font-weight-medium
            | Someone from our team will reach out to you.
            br
            | Keep your phone handy
          p.text-gray-800.mt-4.mb-5.text-center
            | You can track your claim status from the
            br
            | claims page on the Nova Portal
          n-button.w-50.mb-2(
            buttonText="Back to claims page ->",
            variant="secondary",
            :to="{name: 'claim-details', params: {id: claimAssist.id}}")
          n-button.w-50.mb-5(
            buttonText="View your policy details ->",
            variant="outline-primary",
            :to="{name: 'policy', params: {policyId: claimAssist.model.policyId}}")

    .row.mt-5(v-else)
      .col-sm-12.offset-md-3.col-md-6
        div
          b-card.mb-4.rounded
            .d-flex.justify-content-between.align-items-center
              i.icon-arrow.flex-1.cursor-pointer.text-gray-600(@click="$router.go(-1)")
              span.flex-1.font-hc.text-gray-600(v-if="formType==='claim-intimation-form'") Claim Intimation form
              span.flex-1.font-hc.text-gray-600(
                v-else-if="formType==='reimbursement-claim-form'") Reimbursement Claim Form
              span.flex-1.font-hc.text-gray-600(v-else) Emergency Claim Form
              i.flex-1 &nbsp;
            hr
            div(v-if="policyId")
              form(@submit="submitClaim")
                vue-form-generator(
                  :schema="claimAssist.schemaPart1",
                  :model="claimAssist.model",
                  :options="claimAssist.options",
                  ref="claimForm1")
                div(v-if="isWhatsappEnabled && consentReq")
                  n-contact-consent(v-model="whatsappUserConsent", :is-highlighted="true")
                vue-form-generator(
                  :schema="claimAssist.schemaPart2",
                  :model="claimAssist.model",
                  :options="claimAssist.options",
                  ref="claimForm2")
                n-button.w-100(
                  variant="dark",
                  buttonText="Create a claim request")
            div(v-else)
              b-alert(show, variant="warning")
                | You do not have a insurance policy assigned to your account yet. Please contact us for further help
</template>

<script>
import gql from "graphql-tag";
import { mapState, mapGetters } from "vuex";
import moment from "moment";
import userDefs from "../../admin/definitions";
import { claimDocumentsList } from "../../admin/constants";
import { getContentFromPolicyClaimContent } from "../../../../utils/misc";
import utils from "@/utils";
import NUploadComponent from "@/components/FileUpload";
import NContactConsent from "@/components/NovaContactConsent";
import NButton from "@/components/NovaButton";

const getCitiesQuery = () => {
  const query = gql`
    query DropdownCities($query: String) {
      dropdownCities(query: $query)
    }
  `;
  return query;
};

export default {
  name: "ClaimAssist",
  components: {
    NButton,
    NUploadComponent,
    NContactConsent,
  },
  data() {
    const data = {
      eligibleDependents: [],
      claimIntimationStatus: null,
      claimDocumentsList,
      userId: null,
      formSubmitted: false,
      policyId: null,
      status: null,
      upload: false,
      formType: null,
      claimAssist: {
        id: null,
        model: {
          policyId: null,
          patientId: null,
          meta: {
            emergency: false,
            category: "",
            diagnosis: "",
            city: null,
            hospital: null,
            contact: "",
            details: "",
            doa: "",
            dod: "",
            estimatedClaimAmount: null,
            files: [],
            formType: null,
          },
        },
        schemaPart1: {
          fields: [
            {
              model: "policyId",
              type: "inline-select",
              label: "Policy",
              imageIcon: "claims-1",
              validator: [utils.validateInlineSelect],
              options: [
                ...this.$store.state.user.benefits
                  .filter((p) => p.node.isPolicy && ["gmc", "ppc"].includes(p.node.type) && !this.isPolicyExpired(p))
                  .map((policy) => ({
                    ...policy.node,
                    label: policy.node.name,
                    policyMeta: policy.meta,
                  })),
              ],
              reduce: (obj) => obj.id,
              placeholder: "Select Policy",
              required: true,
            },
            {
              type: "inline-select",
              label: "Patient Name",
              model: "patientId",
              validator: [utils.validateInlineSelect],
              imageIcon: "user-circle",
              reduce: (obj) => obj.id,
              options: [
                {
                  label: this.$store.state.user.name,
                  id: this.$store.state.user.id,
                },
                ...(this.eligibleDependents || []).map((dep) => {
                  return {
                    label: dep.name,
                    id: dep.id,
                  };
                }),
              ],
              placeholder: "Select patient",
              required: true,
            },
            {
              model: "meta",
              type: "object",
              validator: utils.validateChildFields,
              schema: {
                fields: [
                  {
                    model: "city",
                    type: "editable-dropdown",
                    label: "City of treatment (Optional)",
                    imageIcon: "city",
                    placeholder: "Select a city",
                    gqlQuery: getCitiesQuery(),
                    transform: (v) => v.name,
                    queryName: "dropdownCities",
                    required: true,
                    taggable: true,
                  },
                  {
                    model: "contact",
                    type: "inline-input",
                    inputType: "tel",
                    label: "Where should we call?",
                    validator: [utils.validateIndianMobileNumbersForVfg],
                    required: true,
                    placeholder: "Enter mobile number",
                    styleClasses: ["mb-0"],
                  },
                ],
              },
            },
          ],
        },
        schemaPart2: ((formType) => {
          const fields = [
            {
              model: "meta",
              type: "object",
              validator: [utils.validateChildFields],
              schema: {
                fields: [
                  {
                    model: "hospital",
                    type: "editable-dropdown",
                    label: "Hospital (Optional)",
                    imageIcon: "hospital",
                    placeholder: "Select Hospital",
                    gqlQuery: userDefs.hospitals.listQuery,
                    transform: (value) => value.name,
                    queryName: "hospitals",
                    taggable: true,
                    additionalVariables: () => {
                      return {
                        city: this.claimAssist.model.meta.city?.name,
                      };
                    },
                  },
                  {
                    model: "diagnosis",
                    type: "text-area",
                    inputType: "text",
                    label: "Procedure or Diagnosis Details (Optional)",
                    placeholder: "Enter procedure details",
                    fieldClasses: ["input-wrapper"],
                  },
                  {
                    model: "details",
                    type: "text-area",
                    inputType: "text",
                    label: "Any additional details? (Optional)",
                    placeholder: "Enter additional details",
                    fieldClasses: ["input-wrapper"],
                  },
                ],
              },
            },
          ];
          if (formType === "claim-intimation-form") {
            fields[0].schema.fields.splice(1, 0, {
              model: "doa",
              type: "datepicker",
              label: "Estimated Hospitalization Date",
              placeholder: "Select estimated hospitalization date",
              min: "1900-01-01",
              max: "2100-12-31",
              required: true,
              subText: "This should be the date you plan on visiting the hospital",
            });
            fields[0].schema.fields.splice(2, 0, {
              model: "doctor",
              type: "inline-input",
              imageIcon: "user-circle",
              inputType: "text",
              label: "Treating Doctor (Optional)",
              placeholder: "Enter treating doctor's name",
            });
            fields[0].schema.fields.splice(3, 0, {
              model: "estimatedClaimAmount",
              type: "inline-input",
              imageIcon: "rupee",
              inputType: "number",
              label: "Estimated Medical Expenses (Optional)",
              placeholder: "Enter expense estimate here",
            });
          }
          if (formType === "reimbursement-claim-form") {
            fields[0].schema.fields.splice(1, 0, {
              model: "doa",
              type: "datepicker",
              label: "Hospitalization Date",
              placeholder: "Select Hospitalization Date",
              min: "1900-01-01",
              max: moment().format("YYYY-MM-DD"),
              required: true,
              subText: "This should be the date you had visited the hospital",
              validator: [this.validateIsDateFuture],
            });
            fields[0].schema.fields.splice(2, 0, {
              model: "doctor",
              type: "inline-input",
              imageIcon: "user-circle",
              inputType: "text",
              label: "Treating Doctor",
              placeholder: "Enter treating doctor's name",
            });
            fields[0].schema.fields.splice(3, 0, {
              model: "estimatedClaimAmount",
              type: "inline-input",
              imageIcon: "rupee",
              inputType: "number",
              label: "Medical Expenses",
              placeholder: "Enter your expense here",
            });
            fields[0].schema.fields.splice(1, 0, {
              model: "category",
              type: "select-cards",
              label: "What kind of treatment did you undergo?",
              cardType: "full-width",
              cardsData: [
                {
                  name: "category",
                  icon: "hr24-store",
                  iconColor: "red-400",
                  card_value: "daycare",
                  description:
                    "These are treatments and procedures that require Hospitalization for less than 24-hours",
                  label: "Day Care Procedure",
                },
                {
                  name: "category",
                  icon: "bed",
                  iconColor: "blue-400",
                  card_value: "hospitalization",
                  description:
                    "Select this option if you think you’ll have to be hospitalized for longer than 24-hours",
                  label: "Hospitalization",
                },
              ],
              required: true,
            });
          }
          return { fields };
        })(this.$route.path.replace("/user/claims/", "")),
        options: {
          validateAfterLoad: false,
          validateAfterChanged: false,
          validateAsync: true,
        },
      },
      consentReq: false,
      whatsappUserConsent: true,
      defaultWhatsappConsent: null,
      isDateRangeError: false,
      isDateFutureError: false,
      claimFormsLink: null,
      claimId: null,
    };
    return data;
  },
  computed: {
    ...mapState(["user"]),
    ...mapGetters(["getFeatureFlags"]),
    isWhatsappEnabled() {
      return this.getFeatureFlags.CLAIMS_WHATSAPP;
    },
    contactNumber() {
      return this.$store.state.user.meta.contactNumber;
    },
  },
  watch: {
    "claimAssist.model.meta.category": function () {
      if (this.claimAssist.model?.meta?.category === "hospitalization") {
        this.claimAssist.schemaPart2.fields[0].schema.fields.splice(2, 1, {
          model: "hospitalization",
          type: "date-range",
          label: "Hospitalization Date",
          placeholder: "Select Hospitalization Date",
          min: "1900-01-01",
          max: moment().format("YYYY-MM-DD"),
          value: null,
          required: true,
          hint: "This should be the date you had visited the hospital",
          validator: [this.validateDateRange],
        });
      } else {
        this.claimAssist.schemaPart2.fields[0].schema.fields.splice(2, 1, {
          model: "doa",
          type: "datepicker",
          label: "Hospitalization Date",
          placeholder: "Select Hospitalization Date",
          min: "1900-01-01",
          max: moment().format("YYYY-MM-DD"),
          required: true,
          subText: "This should be the date you had visited the hospital",
          validator: [this.validateIsDateFuture],
        });
      }
    },
    "claimAssist.model.policyId": function () {
      this.eligibleDependents = [];
      const selectedPolicyId = this.claimAssist?.model?.policyId;
      this.$store.state?.user?.dependents?.map((dep) =>
        dep?.benefits?.every((ben) => {
          if (selectedPolicyId === ben?.node?.id) {
            this.eligibleDependents.push(dep);
            return false;
          }
          return true;
        })
      );
      if (selectedPolicyId === "None selected") return;
      // Reset selected patient each time selection of policy changes
      this.claimAssist.model.patientId = "None selected";
      const selectedPolicy = this.user.benefits.find((ben) => {
        return ben.node.id === selectedPolicyId;
      });
      if (selectedPolicy.node.type === "gpa") {
        const newOptions = {
          label: this.user.name,
          id: this.user.id,
        };
        this.claimAssist.schemaPart1.fields[1].options = [newOptions];
      } else {
        this.claimAssist.schemaPart1.fields[1].options = [
          {
            label: this.user.name,
            id: this.user.id,
          },
          ...this.eligibleDependents.map((dep) => {
            return {
              label: dep.name,
              id: dep.id,
            };
          }),
        ];
      }
    },
    "claimAssist.model.meta.contact": function (value) {
      if (this.contactNumber && this?.defaultWhatsappConsent) {
        this.consentReq = this.isConsentReq(value);
      }
    },
  },
  mounted() {
    const policyId = this.$route.query?.id;
    if (policyId) {
      this.claimAssist.model.policyId = policyId;
    }
    this.formType = this.$route.path.replace("/user/claims/", "");
    if (this.formType === "emergency-claim-form") {
      this.claimAssist.model.meta.emergency = true;
    }
    this.claimAssist.model.meta.contact = this.contactNumber;
  },
  methods: {
    isConsentReq(contact) {
      if (contact === this.contactNumber && this.defaultWhatsappConsent.consent) {
        return false;
      } else {
        return true;
      }
    },
    isPolicyExpired(policy) {
      if (!policy.node.meta.endDate) {
        return false;
      }
      return utils.isExpired(policy.node.meta.endDate);
    },
    downloadEcard() {
      let currentPolicy = this.$store.state.user.benefits.filter(
        (policy) => policy.node.id === this.claimAssist.model.policyId
      );
      currentPolicy = currentPolicy[0];
      if (currentPolicy.meta.cardUrl) {
        window.open(currentPolicy.meta.cardUrl, "_blank");
      }
    },
    async submitClaim(e) {
      this.isDateRangeError = false;
      e.preventDefault();
      const isValid = await this.validateForm();
      if (!isValid) {
        this.$store.commit("addAlert", {
          variant: "danger",
          message: "Please enter valid details before continuing to the next step",
        });
        if (this.isDateRangeError) {
          this.$store.commit("addAlert", {
            variant: "danger",
            message: "Date of Discharge can't be before Date of Admission",
          });
          return;
        }
        if (this.isDateFutureError) {
          this.$store.commit("addAlert", {
            variant: "danger",
            message: "A future date can't be selected",
          });
          return;
        }
        return;
      }
      try {
        const formTypeMapping = {
          "claim-intimation-form": "planned",
          "reimbursement-claim-form": "discharged",
          "emergency-claim-form": "emergency",
        };
        this.claimAssist.model.meta.formType = formTypeMapping[this.formType];
        if (this.isWhatsappEnabled) {
          this.claimAssist.model.meta.claimsWhatsappUserConsent = !this.consentReq
            ? this.defaultWhatsappConsent?.value
            : this.whatsappUserConsent;
        }
        if (this.claimAssist.model.meta.category === "hospitalization") {
          this.claimAssist.model.meta.doa = this.claimAssist.model.meta.hospitalization?.startDate;
          this.claimAssist.model.meta.dod = this.claimAssist.model.meta.hospitalization?.endDate;
        }
        this.claimIntimationStatus = "loading";
        const variables = {
          meta: this.claimAssist.model.meta,
          policyId: this.claimAssist.model.policyId,
          id: this.claimAssist.id,
          userId: this.userId,
          dependentId: null,
        };
        if (this.userId !== this.claimAssist.model.patientId) {
          variables.dependentId = this.claimAssist.model.patientId;
        }
        const result = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateClaim($meta: JSONObject, $policyId: ID, $id: ID, $userId: ID, $dependentId: ID) {
              upsertClaim(
                input: { meta: $meta, policyId: $policyId, id: $id, userId: $userId, dependentId: $dependentId }
              ) {
                claim {
                  id
                  policy {
                    id
                  }
                }
              }
            }
          `,
          variables: variables,
        });
        this.claimIntimationStatus = "success";
        this.$store.commit("addAlert", {
          variant: "success",
          message:
            "Your claim intimation was successfully recorded by us. We will reach out to you for further actions",
        });
        this.claimAssist.id = result.data.upsertClaim.claim.id;
        this.formSubmitted = true;
        this.policyId = result.data.upsertClaim.claim.policy.id;
        await this.$apollo.queries.policy.refetch();
      } catch (err) {
        // gql errors handled globally
        this.claimIntimationStatus = "failure";
      }
    },
    async validateForm() {
      let isValid = true;
      if (this.$refs.claimForm1 && this.$refs.claimForm2) {
        await this.$refs.claimForm1.validate();
        await this.$refs.claimForm2.validate();
        isValid = this.$refs.claimForm1.errors.length < 1 && this.$refs.claimForm2.errors.length < 1;
      }
      return isValid;
    },
    validateDateRange(range) {
      const { endDate, startDate } = range;
      if (moment(endDate).isBefore(moment(startDate))) {
        this.isDateRangeError = true;
        return ["Date of Discharge can't be before Date of Admission"];
      }
    },
    validateIsDateFuture(date) {
      this.isDateFutureError = utils.isFuture(date);
      if (this.isDateFutureError) return ["A future date can't be selected"];
      return [];
    },
    shouldShowLink(docName) {
      const claimForms = ["Claim form: Part A", "Claim form: Part B"];
      return claimForms.includes(docName) && this.claimFormsLink;
    },
  },
  apollo: {
    policyId: {
      query: gql`
        query MyPoliciesQuery {
          me {
            id
            benefits {
              node {
                id
                name
                type
                isPolicy
              }
            }
          }
        }
      `,
      update(data) {
        const policies = data.me.benefits.filter((p) => p.node.isPolicy);
        if (policies.length) return policies[0].node.id;
        else return null;
      },
    },
    user: {
      query: gql`
        query LoggedInUser {
          me {
            id
            claimsWhatsappUserConsent
          }
        }
      `,
      update(data) {
        this.userId = data?.me?.id;
        this.defaultWhatsappConsent = data.me.claimsWhatsappUserConsent;
        if (this.defaultWhatsappConsent.consent && !this.defaultWhatsappConsent?.value) {
          this.consentReq = true;
          this.whatsappUserConsent = this.defaultWhatsappConsent.value;
        } else if (!this.defaultWhatsappConsent.consent) {
          this.consentReq = true;
        } else {
          this.consentReq = false;
        }
      },
    },
    policy: {
      query: userDefs.policies.singleQuery,
      skip() {
        return !this.policyId;
      },
      variables() {
        return { id: this.policyId };
      },
      update(data) {
        const reimbursement = getContentFromPolicyClaimContent(data.node?.policyClaimContent, "Reimbursement");
        if (reimbursement) {
          this.claimFormsLink = reimbursement.claimFormLink;
        } else if (data.node?.reimbursementContent) {
          this.claimFormsLink = data.node?.reimbursementContent?.dynamicContent?.[0]?.claimFormsLink;
        } else {
          this.claimFormsLink = data.node.content?.dynamicContent?.[0]?.claimFormsLink;
        }
        return data.node;
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  background-color: #fbfbfb;
  height: 100%;
  width: 100%;
  overflow: auto;
  z-index: 1000;
}
</style>
